import { AppBar, Box, Button, ClickAwayListener, Grid2, IconButton, Paper, Popper, Stack, Toolbar, Typography, useTheme } from "@mui/material";
import { memo, useRef, useState } from "react";
import { icon } from "@/app/style/icons";
import { Label } from "@/types/enums";
import { RouteTo } from "@routing/RouteMap";
import { useOnMobile } from "@core/hooks/useOnMobile";
import hex2rgba from "hex2rgba";
import { useLocation, useNavigate } from "react-router-dom";
import UserMenu from "./UserMenu";
import { useIsAuthenticated } from "@auth/hooks/useIsAuthenticated";
import LogoNew from "@core/components/LogoNew";
import { NotificationBadge } from "../../../ui-kit/NotificationBadge";

/**
 * Wird im {@link Layout} immer ganz oben angezeigt.
 *
 * On Mobile ist zusätzlich edin Drawer im Einsatz.
 */
const Navbar = memo(() => {
    const theme = useTheme();
    const onMobile = useOnMobile();
    const isAuthenticated = useIsAuthenticated();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const buttonRef = useRef<HTMLDivElement>(null);

    const [isKnowledgePageDropdownOpen, setIsKnowledgePageDropdownOpen] = useState(false);

    // Hide central navbar content on login/auth pages and public legal pages (privacy policy, AGB, legal notice)
    const isLegalPage = pathname === RouteTo.PRIVACY || pathname === RouteTo.AGB || pathname === RouteTo.LEGAL;
    const hideNavbarContent = isLegalPage || pathname === RouteTo.LOGIN || pathname.startsWith("/auth/") || pathname.startsWith("/verify-email");

    // Determine special routes
    const isLoginPage = pathname === RouteTo.LOGIN || pathname.startsWith("/auth/") || pathname.startsWith("/verify-email");
    const isOnboardingPage = pathname === RouteTo.ONBOARDING;
    const isShopPage = pathname === RouteTo.SHOP;

    // Decide whether UserMenu should be hidden
    const hideUserMenu = isLoginPage || isOnboardingPage || isShopPage;

    // Decide whether the logo should be clickable
    const isLogoClickable = !(isLoginPage || isOnboardingPage || isShopPage);

    const NavButton = ({ label, route }: { label: string; route: RouteTo }) => {
        return (
            <Button
                variant={"text"}
                sx={{ px: 2, background: pathname.startsWith(route) ? hex2rgba(theme.palette.semantic.button.primary, 0.2) : undefined }}
                onClick={() => navigate(route)}
            >
                {label}
            </Button>
        );
    };

    const KnowledgeNavButton = () => (
        <div>
            <Box ref={buttonRef}>
                <Button
                    variant="text"
                    onClick={() => setIsKnowledgePageDropdownOpen(!isKnowledgePageDropdownOpen)}
                    sx={{
                        px: 2,
                        background:
                            // pathname.includes(RouteTo.WESENTLICHKEIT) ||
                            pathname.includes(RouteTo.COMPASS) ||
                            pathname.includes(RouteTo.LEXIKON) ||
                            pathname.includes(RouteTo.VIDEOS) ||
                            pathname.includes(RouteTo.USER_GUIDE)
                                ? hex2rgba(theme.palette.semantic.button.primary, 0.2)
                                : undefined,
                    }}
                    endIcon={<icon.dropdown />}
                >
                    {Label.KNOWLEDGE}
                </Button>
            </Box>

            <Popper
                open={isKnowledgePageDropdownOpen}
                anchorEl={buttonRef.current}
                placement="bottom"
                modifiers={[{ name: "offset", options: { offset: [window.innerWidth / 2 - 8, 70] } }]}
                sx={{ zIndex: 1200 }} // Set above AppBar's z-index
            >
                <ClickAwayListener onClickAway={() => setIsKnowledgePageDropdownOpen(false)}>
                    <Paper
                        sx={{
                            p: 1.5,
                            minWidth: "180px",
                            bgcolor: "semantic.surface.secondary",
                            border: `1px solid ${hex2rgba(theme.palette.semantic.button.primary, 0.25)}`,
                            borderRadius: 1,
                        }}
                        elevation={0}
                    >
                        <Stack spacing={0}>
                            <Button
                                onClick={() => {
                                    navigate(RouteTo.USER_GUIDE);
                                    setIsKnowledgePageDropdownOpen(false);
                                }}
                            >
                                <Typography variant={"body2"} textTransform={"none"}>
                                    Nutzerleitfaden
                                </Typography>
                            </Button>

                            <Button
                                onClick={() => {
                                    navigate(RouteTo.COMPASS);
                                    setIsKnowledgePageDropdownOpen(false);
                                }}
                            >
                                <Typography variant={"body2"} textTransform={"none"}>
                                    EU-Taxonomie-Kompass
                                </Typography>
                            </Button>

                            <Button
                                onClick={() => {
                                    navigate(RouteTo.LEXIKON);
                                    setIsKnowledgePageDropdownOpen(false);
                                }}
                            >
                                <Typography variant={"body2"} textTransform={"none"}>
                                    Gesetzeslexikon
                                </Typography>
                            </Button>

                            <Button
                                onClick={() => {
                                    navigate(RouteTo.VIDEOS);
                                    setIsKnowledgePageDropdownOpen(false);
                                }}
                            >
                                <Typography variant={"body2"} textTransform={"none"}>
                                    Videos
                                </Typography>
                            </Button>
                        </Stack>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </div>
    );

    return (
        <AppBar position="static" sx={{ width: "100%" }}>
            <Toolbar disableGutters>
                <Grid2 container size={12} justifyContent={"space-between"} alignItems={"center"} spacing={2} mt={-0.25} mb={-0.2}>
                    <Grid2 size={{ xs: "auto", sm: 3 }}>
                        {/*<CompleteLogo onClick={() => navigate(RouteTo.DASHBOARD)} />*/}
                        <LogoNew
                            size={120}
                            onClick={isLogoClickable ? () => navigate(RouteTo.DASHBOARD) : undefined}
                        />
                    </Grid2>

                    {!hideNavbarContent && !onMobile && isAuthenticated && pathname !== RouteTo.ONBOARDING && pathname !== RouteTo.SHOP && (
                        <Grid2 container size={"grow"} spacing={2} justifyContent={"center"}>
                            <Grid2>
                                <NavButton label={Label.CHAT} route={RouteTo.CHAT} />
                            </Grid2>

                            <Grid2>
                                <KnowledgeNavButton />

                                {/*<NavButton label={Label.KNOWLEDGE} route={RouteTo.KNOWLEDGE} />*/}
                            </Grid2>

                            <Grid2>
                                <NavButton label={Label.AI_SERVICES} route={RouteTo.AI_SERVICES} />
                            </Grid2>
                        </Grid2>
                    )}

                    {!hideUserMenu && (
                        <Grid2 container size={{ xs: "grow", sm: 3 }} justifyContent={"end"} mr={-1.25} alignItems={"center"} spacing={1}>
                            {/*<Grid2>*/}
                            {/*    <IconButton onClick={() => dispatch(setShowExtendedView(!showExtendedView))}>*/}
                            {/*        <icon.debug />*/}
                            {/*    </IconButton>*/}
                            {/*</Grid2>*/}
                            {/* // DISABLED FOR NOW
                            isAuthenticated && (
                                <Grid2>
                                    <NotificationBadge />
                                </Grid2>
                            )
                            */}
                            <Grid2>
                                <UserMenu />
                            </Grid2>
                        </Grid2>
                    )}
                </Grid2>
            </Toolbar>
        </AppBar>
    );
});

export default Navbar;
